import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Plus, X, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function GalleryField({ label, value, onChange, max = 8, testid }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const photos = value || [];

  const upload = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, max - photos.length);
    if (!files.length) return;
    setBusy(true);
    const urls = [];
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      toast.success(`${urls.length} photo${urls.length > 1 ? "s" : ""} uploaded`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally {
      if (urls.length) await onChange([...photos, ...urls]);
      setBusy(false);
      e.target.value = "";
    }
  };
  const move = (i) => { const s = [...photos]; [s[i - 1], s[i]] = [s[i], s[i - 1]]; onChange(s); };

  return (
    <div className="space-y-1.5" data-testid={testid}>
      <Label className="text-xs text-slate-500">{label} ({photos.length}/{max})</Label>
      <div className="flex flex-wrap gap-2">
        {photos.map((p, i) => (
          <div key={p + i} className="group relative h-16 w-20 overflow-hidden rounded-md border border-slate-200" data-testid={`${testid}-photo-${i}`}>
            <img src={p} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-0 flex items-center justify-center gap-1 bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
              {i > 0 && <button type="button" onClick={() => move(i)} className="rounded bg-white/90 p-1" title="Move left" data-testid={`${testid}-left-${i}`}><ArrowLeft className="h-3 w-3" /></button>}
              <button type="button" onClick={() => onChange(photos.filter((_, j) => j !== i))} className="rounded bg-white/90 p-1 text-red-600" title="Remove" data-testid={`${testid}-remove-${i}`}><X className="h-3 w-3" /></button>
            </div>
          </div>
        ))}
        {photos.length < max && (
          <Button type="button" variant="outline" disabled={busy} onClick={() => ref.current?.click()} className="h-16 w-20 flex-col gap-0.5 text-xs" data-testid={`${testid}-add`}>
            <Plus className="h-4 w-4" /> {busy ? "…" : "Add"}
          </Button>
        )}
        <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={upload} data-testid={`${testid}-file`} />
      </div>
    </div>
  );
}
